'use client';

import { useEffect, useRef, useState } from 'react';
import { PortableText } from '@portabletext/react';
import Autoplay from 'embla-carousel-autoplay';
import AutoScroll from 'embla-carousel-auto-scroll';
import { cn } from '@/lib/utils';
import { GroqImage } from '@/helpers/Image';
import { richTextComponent } from '@/sanity/sanityPortableText';
import {
  Carousel,
  CarouselApi,
  CarouselContent,
  CarouselItem,
} from '@/shadcn/ui/carousel';
import { PageTextOnlyCryptoSchema } from '@/sanity/pages/PageTextOnlyCryptoTemplate';

/** Figma: desktop 7218:447, mobile 8662:24534. */
const AUTOPLAY_DELAY = 5000;
/** Same marquee as the home hero quotes (HeroQuotesCarousel). */
const EDGE_FADE =
  'linear-gradient(to right, transparent 0%, black 10%, black 90%, transparent 100%)';

export default function ProofsSection({
  data,
  className,
}: {
  data: PageTextOnlyCryptoSchema;
  className?: string;
}) {
  const certificates = (data?.proofsCertificates ?? []).filter(
    (certificate) => certificate?.src,
  );
  const [api, setApi] = useState<CarouselApi>();
  const [current, setCurrent] = useState(0);
  const autoplay = useRef(Autoplay({ delay: AUTOPLAY_DELAY, stopOnInteraction: false }));
  const fillRef = useRef<HTMLSpanElement>(null);
  const testimonials = data?.proofsTestimonials ?? [];
  const autoScroll = useRef(
    AutoScroll({ speed: 1, startDelay: 0, stopOnInteraction: false }),
  ).current;

  useEffect(() => {
    if (!api) return;
    const onSelect = () => setCurrent(api.selectedScrollSnap());
    onSelect();
    api.on('select', onSelect);
    api.on('reInit', onSelect);
    return () => {
      api.off('select', onSelect);
      api.off('reInit', onSelect);
    };
  }, [api]);

  useEffect(() => {
    if (!api || certificates.length < 2) return;
    let frame = requestAnimationFrame(function tick() {
      const node = fillRef.current;
      if (node) {
        const left = autoplay.current.timeUntilNext();
        const filled =
          left === null ? 1 : Math.min(1, Math.max(0, 1 - left / AUTOPLAY_DELAY));
        node.style.transform = `scaleX(${filled})`;
      }
      frame = requestAnimationFrame(tick);
    });
    return () => cancelAnimationFrame(frame);
  }, [api, certificates.length]);

  return (
    <section
      id="chapter-2"
      data-chapter
      className={cn(
        'relative flex justify-center w-full bg-white px-[2rem] lg:px-0 pt-[8rem] lg:pt-[10rem] pb-[8rem] lg:pb-[12.5rem]',
        className,
      )}
    >
      <div className="flex flex-col w-[min(94.5rem,100%)] overflow-clip rounded-[1rem] border border-[#001B14]/10 bg-[#F7F7F7]">
        {data?.proofsIntro && (
          <div
            className="richtext richtext-start w-full max-lg:px-[2rem] max-lg:pt-[3rem] max-lg:pb-[2rem] lg:p-[5.25rem] lg:h-[18.75rem]
            prose-p:font-geist prose-p:font-medium max-lg:prose-p:text-[3.5rem] lg:prose-p:text-[4rem] prose-p:leading-[1.12] prose-p:text-[#001B14] prose-p:mb-0
            max-lg:prose-p:w-[min(41rem,100%)]
            [&_span]:italic [&_span]:font-normal max-lg:[&_span]:leading-[0.85]"
          >
            <PortableText
              value={data.proofsIntro}
              components={richTextComponent}
            />
          </div>
        )}

        <div className="flex flex-col max-lg:gap-[4rem] lg:gap-[5.75rem] w-full max-lg:px-[2rem] lg:px-[5.25rem] max-lg:pt-0 lg:pt-[4rem] max-lg:pb-[3rem] lg:pb-[5.25rem]">
          {certificates.length > 0 && (
            <div className="flex flex-col items-center gap-[3.276rem] w-full">
              <Carousel
                setApi={setApi}
                opts={{ loop: certificates.length > 1 }}
                plugins={certificates.length > 1 ? [autoplay.current] : []}
                className="w-[min(83.682rem,100%)]"
              >
                <CarouselContent className="w-full">
                  {certificates.map((certificate, index) => (
                    <CarouselItem
                      key={`${certificate.src}-${index}`}
                      className="basis-full !pl-0"
                    >
                      <div className="relative w-full aspect-[669.454/459.988] overflow-clip max-lg:rounded-[0.322rem] lg:rounded-[0.546rem]">
                        <GroqImage
                          src={certificate}
                          alt="Payout certificate"
                          quality={100}
                          className="absolute inset-0 !w-full !h-full object-cover"
                          style={{ width: '100%', height: '100%' }}
                        />
                      </div>
                    </CarouselItem>
                  ))}
                </CarouselContent>
              </Carousel>

              <div className="flex w-[min(83.75rem,100%)] h-[0.273rem] gap-[1.092rem]">
                {certificates.map((certificate, index) => (
                  <button
                    key={`${certificate.src}-${index}`}
                    type="button"
                    aria-label={`Certificate ${index + 1}`}
                    onClick={() => {
                      api?.scrollTo(index);
                      autoplay.current.reset();
                    }}
                    className={cn(
                      'relative h-full min-w-0 overflow-hidden bg-black/10 transition-[flex-grow] duration-500',
                      index === current
                        ? 'max-lg:flex-[9.633] lg:flex-[6.637]'
                        : 'flex-1',
                    )}
                  >
                    {index === current && (
                      <span
                        aria-hidden
                        ref={fillRef}
                        className={cn(
                          'absolute inset-0 origin-left bg-black will-change-transform',
                          certificates.length > 1 ? 'scale-x-0' : 'scale-x-100',
                        )}
                      />
                    )}
                  </button>
                ))}
              </div>
            </div>
          )}

          <div className="flex flex-col gap-[4rem] w-full">
            {data?.proofsText && (
              <div
                className="richtext richtext-start w-full lg:w-[73.5rem]
                prose-p:font-geist prose-p:font-normal prose-p:text-[2.25rem] prose-p:leading-[1.4] prose-p:tracking-[-0.0675rem] prose-p:text-black prose-p:opacity-80 prose-p:mb-0 lg:prose-p:w-[68.125rem]"
              >
                <PortableText
                  value={data.proofsText}
                  components={richTextComponent}
                />
              </div>
            )}

            {testimonials.length > 0 && (
              <div
                className="w-full overflow-hidden"
                style={{ maskImage: EDGE_FADE, WebkitMaskImage: EDGE_FADE }}
              >
                <Carousel opts={{ loop: true }} plugins={[autoScroll]} className="w-full">
                  <CarouselContent className="-ml-[0.75rem]">
                    {[...testimonials, ...testimonials, ...testimonials].map((item, index) => (
                      <CarouselItem
                        key={`${item?._key ?? 'testimonial'}-${index}`}
                        className="basis-auto pl-[0.75rem]"
                      >
                        <div className="flex items-center gap-[2rem] lg:gap-[3.375rem] h-full w-[36rem] lg:w-[52rem] bg-[#EDEDED] p-[2rem]">
                          {item?.image && (
                            <div className="relative w-[5rem] h-[5rem] lg:w-[6.625rem] lg:h-[6.625rem] shrink-0 overflow-clip rounded-[0.25rem]">
                              <GroqImage
                                src={item.image}
                                alt={item?.author ?? ''}
                                quality={100}
                                className="absolute inset-0 !w-full !h-full object-cover"
                                style={{ width: '100%', height: '100%' }}
                              />
                            </div>
                          )}
                          <div className="flex flex-col min-w-0">
                            {item?.quote && (
                              <p className="mb-0 font-geist font-semibold text-[2rem] leading-[1.33] tracking-[-0.06rem] text-black">
                                {item.quote}
                              </p>
                            )}
                            {item?.author && (
                              <p className="mb-0 mt-[0.625rem] font-geist-mono font-medium text-[1.75rem] leading-[1.33] uppercase text-black/50">
                                {item.author}
                              </p>
                            )}
                          </div>
                        </div>
                      </CarouselItem>
                    ))}
                  </CarouselContent>
                </Carousel>
              </div>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}
